import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Card, CardActionArea, Divider, Paper, Typography } from '@mui/material';
import Footer from '../components/homepage/Footer';
import LayersOutlinedIcon from '@mui/icons-material/LayersOutlined';
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import { useAuth } from '../context/AuthContext';
import { usePermissions, FINANCE_SPACE_ID } from '../hooks/usePermissions';
import logo from '../assets/logo.png';

const ADMIN_SPACE   = { spaceId: '__admin__',   roles: [], _isAdmin:   true };
const FINANCE_SPACE = { spaceId: FINANCE_SPACE_ID, roles: [], _isFinance: true };

export default function SpaceSelect() {
  const { user, setActiveSpace } = useAuth();
  const { isSystemUser, isGlobalAdmin, isEntityAdmin, isGmsAccountant, systemRole } = usePermissions();
  const navigate = useNavigate();

  const spaces = user?.spaces ?? [];

  const handleSelect = (space) => {
    setActiveSpace(space);
    navigate('/', { replace: true });
  };

  // Non-system, non-accountant users with exactly one space skip the selection screen.
  useEffect(() => {
    if (!isSystemUser && !isGmsAccountant && spaces.length === 1) {
      handleSelect(spaces[0]);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Box
      sx={{
        height: ['100vh', '100dvh'],
        bgcolor: '#0a326d',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          flexGrow: 1,
          minHeight: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 2,
          pb: { xs: 9, sm: 10 },
        }}
      >
      <Paper
        elevation={8}
        sx={{
          p: { xs: 3, sm: 5 },
          borderRadius: 4,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 3,
          width: { xs: '100%', sm: 480 },
          maxWidth: 480,
          maxHeight: '100%',
          bgcolor: '#ffffff',
        }}
      >
        <Box component="img" src={logo} alt="TifoXR" sx={{ height: 48, objectFit: 'contain', flexShrink: 0 }} />

        <Box sx={{ textAlign: 'center', flexShrink: 0 }}>
          <Typography variant="h6" fontWeight={800} color="#0a326d">
            {isSystemUser ? 'Where would you like to go?' : 'Select a Space'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Choose which space you want to manage.
          </Typography>
        </Box>

        <Divider sx={{ width: '100%', flexShrink: 0 }} />

        <Box sx={{
          width: '100%',
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
          pr: 0.5,
          '&::-webkit-scrollbar': { width: 4 },
          '&::-webkit-scrollbar-track': { background: 'transparent' },
          '&::-webkit-scrollbar-thumb': { background: 'rgba(83,131,179,0.25)', borderRadius: 4 },
        }}>

          {/* Admin Portal card — only for global_admin and entity_admin */}
          {(isGlobalAdmin || isEntityAdmin) && (
            <>
              <Card
                elevation={0}
                sx={{
                  flexShrink: 0,
                  border: '1px solid #c7d7f0',
                  borderRadius: 2,
                  bgcolor: '#f0f5ff',
                  '&:hover': { borderColor: '#0a326d', boxShadow: '0 2px 8px rgba(10,50,109,0.15)' },
                  transition: 'all 0.15s',
                }}
              >
                <CardActionArea onClick={() => handleSelect(ADMIN_SPACE)} sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{ p: 1, bgcolor: '#0a326d', borderRadius: 1.5, display: 'flex' }}>
                    <AdminPanelSettingsOutlinedIcon sx={{ fontSize: 22, color: '#ffffff' }} />
                  </Box>
                  <Box sx={{ flexGrow: 1, textAlign: 'left' }}>
                    <Typography variant="body1" fontWeight={700} color="#0a326d">
                      Admin Portal
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'capitalize' }}>
                      {systemRole?.replace(/_/g, ' ') || 'Admin'}
                    </Typography>
                  </Box>
                </CardActionArea>
              </Card>
              {spaces.length > 0 && (
                <Divider sx={{ flexShrink: 0 }}>
                  <Typography variant="caption" sx={{ color: '#94a3b8', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.8, px: 1 }}>
                    Spaces
                  </Typography>
                </Divider>
              )}
            </>
          )}

          {/* Finance Portal card — only for gms_accountant */}
          {isGmsAccountant && (
            <>
              <Card
                elevation={0}
                sx={{
                  flexShrink: 0,
                  border: '1px solid #a7f3d0',
                  borderRadius: 2,
                  bgcolor: '#ecfdf5',
                  '&:hover': { borderColor: '#059669', boxShadow: '0 2px 8px rgba(5,150,105,0.15)' },
                  transition: 'all 0.15s',
                }}
              >
                <CardActionArea onClick={() => handleSelect(FINANCE_SPACE)} sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{ p: 1, bgcolor: '#059669', borderRadius: 1.5, display: 'flex' }}>
                    <AccountBalanceOutlinedIcon sx={{ fontSize: 22, color: '#ffffff' }} />
                  </Box>
                  <Box sx={{ flexGrow: 1, textAlign: 'left' }}>
                    <Typography variant="body1" fontWeight={700} color="#065f46">
                      Finance Portal
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'capitalize' }}>
                      {systemRole?.replace(/_/g, ' ') || 'GMS Accountant'}
                    </Typography>
                  </Box>
                </CardActionArea>
              </Card>
              {spaces.length > 0 && (
                <Divider sx={{ flexShrink: 0 }}>
                  <Typography variant="caption" sx={{ color: '#94a3b8', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.8, px: 1 }}>
                    Spaces
                  </Typography>
                </Divider>
              )}
            </>
          )}

          {/* Actual space membership cards */}
          {spaces.map((space) => (
            <Card
              key={space.spaceId}
              elevation={0}
              sx={{
                flexShrink: 0,
                border: '1px solid #e2e8f0',
                borderRadius: 2,
                '&:hover': { borderColor: '#0a326d', boxShadow: '0 2px 8px rgba(10,50,109,0.12)' },
                transition: 'all 0.15s',
              }}
            >
              <CardActionArea onClick={() => handleSelect(space)} sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ p: 1, bgcolor: '#f1f5f9', borderRadius: 1.5, display: 'flex' }}>
                  <LayersOutlinedIcon sx={{ fontSize: 22, color: '#475569' }} />
                </Box>
                <Box sx={{ flexGrow: 1, textAlign: 'left' }}>
                  <Typography variant="body1" fontWeight={700} color="#0a326d">
                    {space.spaceName || `Space ${space.spaceId}`}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {(space.roles ?? []).map((r) => r.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())).join(', ') || 'No roles assigned'}
                  </Typography>
                </Box>
              </CardActionArea>
            </Card>
          ))}

        </Box>
      </Paper>
      </Box>

      <Footer dark />
    </Box>
  );
}
